/**
 * Comité activo en la barra superior. Si el usuario tiene acceso de oficina
 * a otros comités (Console → accesos), se vuelve un selector: al elegir otro,
 * el backend emite un JWT de ese comité y se recarga el admin (los datos en
 * caché son del comité anterior). Un token = un comité.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, Building2, Loader2 } from "lucide-react";

import { cambiarComite, listarMisComites } from "../features/auth/api";
import { useAuth } from "../features/auth/useAuth";
import { ApiError } from "../lib/api";
import type { AuthState } from "../lib/auth";
import { useToast } from "./Toast";

function nombreActual(state: AuthState): string {
  return state.viaLoginAs && state.empresaTargetNombre
    ? state.empresaTargetNombre
    : (state.empresaNombre ?? `Empresa #${state.empresaId}`);
}

export function useCambiarComite(): {
  cambiar: (empresaId: number) => void;
  pendiente: boolean;
} {
  const { login } = useAuth();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: cambiarComite,
    onSuccess: ({ access_token }) => {
      login(access_token);
      window.location.assign("/");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo cambiar de comité"),
  });
  return { cambiar: (id) => mut.mutate(id), pendiente: mut.isPending };
}

export function ComiteSelector({ state }: { state: AuthState }): JSX.Element {
  // El superadmin cambia de comité con login-as (exige motivo), no por acá.
  const comitesQ = useQuery({
    queryKey: ["me", "comites"],
    queryFn: listarMisComites,
    enabled: !state.isSuperadmin,
    staleTime: 5 * 60_000,
  });
  const { cambiar, pendiente } = useCambiarComite();
  const comites = comitesQ.data ?? [];

  if (comites.length <= 1) {
    return (
      <div className="text-right leading-tight">
        <div className="max-w-[220px] truncate font-medium text-neutral-900">
          {nombreActual(state)}
        </div>
        <div className="text-xs capitalize text-neutral-600">{state.role}</div>
      </div>
    );
  }

  return (
    <label className="flex items-center gap-2" title="Cambiar de comité">
      {pendiente ? (
        <Loader2 className="h-4 w-4 animate-spin text-brand-600" aria-hidden />
      ) : (
        <Building2 className="h-4 w-4 text-brand-600" aria-hidden />
      )}
      <span className="sr-only">Comité activo</span>
      <select
        value={state.empresaId}
        disabled={pendiente}
        onChange={(e) => cambiar(Number(e.target.value))}
        className="max-w-[240px] rounded-md border border-neutral-450 bg-white py-1.5 pl-2 pr-8 text-sm font-medium text-neutral-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        {comites.map((c) => (
          <option key={c.empresa_id} value={c.empresa_id}>
            {c.nombre}
            {c.es_origen ? " (mi comité)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Aviso cuando se opera en un comité ajeno con acceso de oficina. */
export function AvisoAccesoOficina({ state }: { state: AuthState }): JSX.Element | null {
  const { cambiar, pendiente } = useCambiarComite();
  if (state.empresaOrigenId === undefined) return null;
  return (
    <div className="sticky top-0 z-40 border-b border-brand-200 bg-brand-100 text-brand-900">
      <div className="flex items-center gap-3 px-4 py-2 text-sm">
        <ArrowLeftRight className="h-4 w-4 shrink-0" aria-hidden />
        <div className="flex-1 leading-tight">
          <span className="font-semibold">Acceso de oficina</span>
          <span className="ml-2 opacity-90">
            Estás operando en <strong>{nombreActual(state)}</strong>. Lo que hagas queda
            registrado a tu nombre en este comité.
          </span>
        </div>
        <button
          type="button"
          disabled={pendiente}
          onClick={() => cambiar(state.empresaOrigenId as number)}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-brand-900/10 px-2.5 py-1 text-xs font-medium hover:bg-brand-900/20 disabled:opacity-60"
        >
          Volver a mi comité
        </button>
      </div>
    </div>
  );
}
